import {
  Button,
  ControlGroup,
  FormGroup,
  InputGroup,
  Popover,
} from "@blueprintjs/core";
import type { ExtraFieldTypeColor } from "@package/ui-gql/index-data-access";
import { get, set } from "lodash";
import { useMemo } from "react";
import { HexColorPicker } from "react-colorful";
import tinycolor from "tinycolor2";
import type { ServiceDataFormFieldBaseProps } from "./ServiceDataFormFieldEmpty";

type ServiceDataFormFieldColorProps = ServiceDataFormFieldBaseProps & {
  field: ExtraFieldTypeColor;
};

function ServiceDataFormFieldColorContent({
  data,
  field,
  onSourceChanged,
}: ServiceDataFormFieldColorProps) {
  const value = useMemo<string>(
    () => get(data, field.source) ?? field.defaultValue ?? "",
    [data, field.defaultValue, field.source],
  );
  const colorButton = useMemo<string | undefined>(
    () =>
      (value && tinycolor(value).getBrightness() < 60 && "#ffffff") ||
      undefined,
    [value],
  );

  return (
    <FormGroup label={field.label}>
      <ControlGroup fill>
        <InputGroup
          fill
          value={value}
          onValueChange={(v) =>
            onSourceChanged((draft) => {
              set(draft, field.source, v);
            })
          }
        />
        <Popover
          position="auto"
          usePortal
          minimal
          canEscapeKeyClose
          content={
            <HexColorPicker
              color={value}
              onChange={(color) =>
                onSourceChanged((draft) => {
                  set(draft, field.source, color);
                })
              }
            />
          }
        >
          <Button
            minimal
            outlined
            icon="color-fill"
            style={{
              backgroundColor: value ?? undefined,
              color: colorButton,
            }}
          />
        </Popover>
      </ControlGroup>
    </FormGroup>
  );
}

export function ServiceDataFormFieldColor({
  field,
  onSourceChanged,
  data,
}: ServiceDataFormFieldBaseProps) {
  if (field.__typename === "ExtraFieldTypeColor") {
    return (
      <ServiceDataFormFieldColorContent
        field={field}
        data={data}
        onSourceChanged={onSourceChanged}
      />
    );
  }

  return null;
}
